import {
  EmailDomainPolicyError,
  mapEmailDomainPolicyErrorToMessage
} from '../emailDomainPolicy';
import { TranslationString } from '../localization';
import { OAuthRegistrationError, RegisterErrors } from '../user';

import { AccessRecoveryError, AccessRecoveryOAuthError } from './server';

export const ACCESS_RECOVERY_COMPLETE_ERROR_CODES = [
  AccessRecoveryError.TOKEN_EXPIRED,
  EmailDomainPolicyError.RECOVERY_EMAIL_DOMAIN_NOT_ALLOWED,
  RegisterErrors.EMAIL_TAKEN
] as const;

export type AccessRecoveryCompleteError =
  (typeof ACCESS_RECOVERY_COMPLETE_ERROR_CODES)[number];

export const ACCESS_RECOVERY_COMPLETE_OAUTH_ERROR_CODES = [
  AccessRecoveryError.TOKEN_EXPIRED,
  EmailDomainPolicyError.RECOVERY_EMAIL_DOMAIN_NOT_ALLOWED,
  RegisterErrors.EMAIL_TAKEN,
  AccessRecoveryOAuthError.PENDING_EXPIRED,
  AccessRecoveryOAuthError.OAUTH_ACCOUNT_ALREADY_LINKED,
  OAuthRegistrationError.GOOGLE_REGISTRATION_FORBIDDEN,
  OAuthRegistrationError.TG_REGISTRATION_FORBIDDEN
] as const;

export type AccessRecoveryCompleteOAuthError =
  (typeof ACCESS_RECOVERY_COMPLETE_OAUTH_ERROR_CODES)[number];

export const ACCESS_RECOVERY_COMPLETE_OAUTH_NON_TOAST_ERROR_CODES = [
  AccessRecoveryError.TOKEN_EXPIRED,
  EmailDomainPolicyError.RECOVERY_EMAIL_DOMAIN_NOT_ALLOWED,
  RegisterErrors.EMAIL_TAKEN
] as const;

export type AccessRecoveryOAuthToastError =
  | AccessRecoveryOAuthError.PENDING_EXPIRED
  | AccessRecoveryOAuthError.OAUTH_ACCOUNT_ALREADY_LINKED
  | OAuthRegistrationError.GOOGLE_REGISTRATION_FORBIDDEN
  | OAuthRegistrationError.TG_REGISTRATION_FORBIDDEN;

export const ACCESS_RECOVERY_CREDENTIALS_TOAST_ERROR_CODES = [
  AccessRecoveryOAuthError.OAUTH_ACCOUNT_ALREADY_LINKED,
  OAuthRegistrationError.GOOGLE_REGISTRATION_FORBIDDEN,
  OAuthRegistrationError.TG_REGISTRATION_FORBIDDEN
] as const;

export type AccessRecoveryCredentialsToastError =
  (typeof ACCESS_RECOVERY_CREDENTIALS_TOAST_ERROR_CODES)[number];

export const isAccessRecoveryCredentialsToastError = (
  code: string
): code is AccessRecoveryCredentialsToastError =>
  ACCESS_RECOVERY_CREDENTIALS_TOAST_ERROR_CODES.includes(
    code as AccessRecoveryCredentialsToastError
  );

export const mapAccessRecoveryOAuthToastErrorToMessage = (
  code: AccessRecoveryOAuthToastError
): TranslationString => {
  if (code === AccessRecoveryOAuthError.PENDING_EXPIRED) {
    return (t) =>
      t('accessRecovery.oauth.pendingExpired', {
        ns: 'authPage'
      });
  }

  if (code === AccessRecoveryOAuthError.OAUTH_ACCOUNT_ALREADY_LINKED) {
    return (t) =>
      t('accessRecovery.oauth.accountAlreadyLinked', {
        ns: 'authPage'
      });
  }

  return (t) =>
    t('accessRecovery.googleModal.title', {
      ns: 'authPage'
    });
};

export const mapAccessRecoveryCompleteOAuthErrorToMessage = (
  code: AccessRecoveryCompleteOAuthError
): TranslationString => {
  if (code === AccessRecoveryError.TOKEN_EXPIRED) {
    return (t) =>
      t('accessRecovery.errors.tokenExpired.link.title', {
        ns: 'authPage'
      });
  }

  if (code === EmailDomainPolicyError.RECOVERY_EMAIL_DOMAIN_NOT_ALLOWED) {
    return mapEmailDomainPolicyErrorToMessage(code);
  }

  if (code === RegisterErrors.EMAIL_TAKEN) {
    return (t) =>
      t('accessRecovery.errors.emailTaken', {
        ns: 'authPage'
      });
  }

  return mapAccessRecoveryOAuthToastErrorToMessage(code);
};
